import { z } from 'zod';
import { IsoTime } from './basics.js';
/**
 * CrowdSec's own health on one server (P11.9.1; knowledge doc section 8.1).
 *
 * A CrowdSec that runs is not a CrowdSec that works: a web server's log in a format
 * no parser knows is read line by line and turned into nothing, and a bouncer that
 * stopped pulling leaves every new ban in CrowdSec's database and none in the
 * firewall. Neither shows anywhere else. This is what does.
 *
 * Two sources, both read on the server:
 *
 * - **CrowdSec's Prometheus endpoint**, read by the agent itself as `bartizo` (C2):
 *   lines read and parsed per acquisition source, buckets poured and overflowed per
 *   scenario. Its address comes from CrowdSec's own configuration, through the
 *   helper, never from a setting.
 * - **`inspect_crowdsec`**, the helper's one read-only, argument-free operation for
 *   what only root and `cscli` know: the bouncers and when each last pulled, the
 *   hub's items and which are tainted, outdated or local.
 *
 * Names verified against CrowdSec 1.8.1 (agent fixtures `crowdsec/1.8.1/metrics.txt`,
 * `hub-list.json`, `bouncers-list.json`).
 */
/** How many of each a report carries (rule 19's spirit: capped, never everything). */
export const CROWDSEC_HEALTH_LIMITS = {
    sources: 50,
    scenarios: 50,
    bouncers: 20,
    collections: 200,
    hubProblems: 100,
    simulated: 1000,
    warnings: 30,
};
/**
 * A bouncer that has not pulled for this long is silent. Bouncers pull every 10 s
 * by default; ten minutes is a stopped service, not a slow one.
 */
export const BOUNCER_SILENT_MS = 10 * 60_000;
/** Printable text from CrowdSec, bounded. Control characters would forge lines in a log. */
const Text = (max) => z
    .string()
    .max(max)
    .regex(/^[^\p{Cc}]*$/u);
/** A hub item's or a local file's name: `author/name`, or what a local file calls itself. */
export const HubItemName = Text(128).min(1);
const Count = z.number().int().nonnegative();
/**
 * One acquisition source: a log file, the journal, … `read`, `parsed` and
 * `unparsed` count since CrowdSec started; the `recent` ones over the report's
 * window, which is what a warning is decided on.
 */
export const CrowdsecSourceHealth = z.object({
    source: Text(512).min(1),
    /** The datasource: `file`, `journalctl`, `docker`, … */
    datasource: Text(64),
    /** The acquisition's `labels.type`: `nginx`, `syslog`, `bartizo-web`, … */
    label: Text(64).nullable(),
    read: Count,
    parsed: Count,
    unparsed: Count,
    recentRead: Count,
    recentParsed: Count,
    recentUnparsed: Count,
});
/** One scenario that has seen events since CrowdSec started. */
export const CrowdsecScenarioHealth = z.object({
    name: HubItemName,
    /** Events poured into its buckets. */
    poured: Count,
    /** Times it fired. */
    overflowed: Count,
});
/** `cscli bouncers list`: what enforces CrowdSec's decisions, and whether it still does. */
export const CrowdsecBouncerHealth = z.object({
    name: Text(128).min(1),
    /** What the bouncer says it is (`crowdsec-firewall-bouncer`, …); empty before its first pull. */
    type: Text(128).nullable(),
    version: Text(64).nullable(),
    lastPull: IsoTime.nullable(),
    revoked: z.boolean(),
});
export const HubItemKind = z.enum([
    'collections',
    'parsers',
    'scenarios',
    'postoverflows',
    'contexts',
    'appsec-configs',
    'appsec-rules',
]);
export const CrowdsecHubItem = z.object({
    kind: HubItemKind,
    name: HubItemName,
    version: Text(32).nullable(),
    /** Edited on the server, so the hub's updates no longer reach it. */
    tainted: z.boolean(),
    /** A newer version is in the hub's index (`update-available`). */
    outdated: z.boolean(),
    /** A file of the server's own, not from the hub — Bartizo's `bartizo/*` scenarios among them. */
    local: z.boolean(),
});
export const CrowdsecHub = z.object({
    /** Every installed collection: P11.9.2 builds on this. */
    collections: z.array(CrowdsecHubItem).max(CROWDSEC_HEALTH_LIMITS.collections),
    /** Other items only when something is to be said: tainted, outdated or local. */
    problems: z.array(CrowdsecHubItem).max(CROWDSEC_HEALTH_LIMITS.hubProblems),
    installed: Count,
    tainted: Count,
    outdated: Count,
    local: Count,
    /**
     * P11.9.2: what Bartizo manages here, from the helper's own record — the
     * collections it installed, the scenarios it put in simulation — and every
     * scenario in simulation, whoever put it there. Absent from older helpers.
     */
    managed: z
        .object({
        collections: z.array(HubItemName).max(CROWDSEC_HEALTH_LIMITS.collections),
        simulation: z.array(HubItemName).max(CROWDSEC_HEALTH_LIMITS.simulated),
    })
        .default({ collections: [], simulation: [] }),
    simulated: z.array(HubItemName).max(CROWDSEC_HEALTH_LIMITS.simulated).default([]),
    /** `simulation: true` in `simulation.yaml`: every scenario watches and nothing bans. */
    globalSimulation: z.boolean().default(false),
});
/**
 * `inspect_crowdsec`'s answer, in the helper outcome's `data`. `metrics` is where
 * CrowdSec's own configuration says its Prometheus endpoint listens, or null when
 * it is switched off.
 */
export const CrowdsecInspection = z.object({
    version: Text(64).nullable(),
    metrics: z
        .object({
        address: Text(64).min(1),
        port: z.number().int().min(1).max(65_535),
    })
        .nullable(),
    bouncers: z.array(CrowdsecBouncerHealth).max(CROWDSEC_HEALTH_LIMITS.bouncers),
    hub: CrowdsecHub,
});
export const InspectCrowdsecArgs = z.object({}).strict();
/**
 * What needs a person, and what is only worth knowing. A `warning` makes the
 * module unhealthy, so the server is on the overview's list and the Servers page's
 * *Needs a look*; a `notice` is shown on the server's CrowdSec health card only.
 */
export const CrowdsecWarningCode = z.enum([
    /** The Prometheus endpoint is off or does not answer: parsing cannot be checked. */
    'metrics_unavailable',
    /** It listens on more than loopback, so anyone who can reach the port can read it. */
    'metrics_exposed',
    /** A source was read and nothing in it parsed: a log format no parser knows. */
    'source_unparsed',
    /** Bartizo's web log is being written and CrowdSec read none of it: P11.5's rules see nothing. */
    'bartizo_log_unread',
    /** A bouncer has not pulled for ten minutes: new bans are not enforced. */
    'bouncer_silent',
    /** No bouncer at all: CrowdSec decides and nothing enforces. */
    'no_bouncer',
    'hub_tainted',
    'hub_outdated',
    /** `inspect_crowdsec` failed: bouncers and the hub could not be checked. */
    'inspect_failed',
]);
export const CrowdsecWarning = z.object({
    code: CrowdsecWarningCode,
    level: z.enum(['warning', 'notice']),
    /** What it is about: a source, a bouncer's name; null when it is about CrowdSec as a whole. */
    subject: Text(512).nullable(),
    message: Text(500).min(1),
});
/** `POST /v1/ingest/crowdsec-health`: the server's latest, replacing the one before. */
export const CrowdsecHealthReport = z
    .object({
    /** The agent's own, so a report sent twice is stored once (rule 7). */
    id: z.uuidv7(),
    takenAt: IsoTime,
    version: Text(64).nullable(),
    metrics: z.object({
        state: z.enum(['ok', 'disabled', 'unreachable', 'unknown']),
        exposed: z.boolean(),
    }),
    /** How far back the `recent` counts reach. */
    windowSeconds: Count,
    sources: z.array(CrowdsecSourceHealth).max(CROWDSEC_HEALTH_LIMITS.sources),
    scenarios: z.array(CrowdsecScenarioHealth).max(CROWDSEC_HEALTH_LIMITS.scenarios),
    /** Null until `inspect_crowdsec` has answered once. */
    inspectedAt: IsoTime.nullable(),
    bouncers: z.array(CrowdsecBouncerHealth).max(CROWDSEC_HEALTH_LIMITS.bouncers).nullable(),
    hub: CrowdsecHub.nullable(),
    warnings: z.array(CrowdsecWarning).max(CROWDSEC_HEALTH_LIMITS.warnings),
})
    .strict();
export const CrowdsecHealthReceipt = z.object({
    /** False for a report already stored, or older than the one the API has. */
    stored: z.boolean(),
});
/** `GET /v1/servers/:id/crowdsec-health`. */
export const CrowdsecHealthResponse = z.object({
    report: CrowdsecHealthReport.nullable(),
    receivedAt: IsoTime.nullable(),
});
//# sourceMappingURL=crowdsec-health.js.map